import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Volume2, Square, Send, Upload, Plus, Play, Mic, MicOff, VolumeX } from 'lucide-react';
// import { getGreeting } from './constants';
import {getGreeting} from '../AiAgent/constants'
import { useVoiceRecognition } from '../AiAgent/voiceRecognitionHook'; // We'll create this hook
import { playWithSpeechSynthesis } from '../AiAgent/voiceRecognitionHook'; // We'll expose this from the hook or as a separate util
import { generateRandomUserId } from '../AiAgent/utils';
import {
  containerStyle, sidebarStyle, headerStyle, buttonStyle, newChatButtonStyle,
  uploadSidebarButtonStyle, statusStyle, visualizationStyle, micContainerStyle,
  chatAreaStyle, chatHeaderStyle, messagesStyle, inputAreaStyle,
  inputContainerStyle, textInputStyle, sendButtonStyle,
  messageContainerStyle, messageContentStyle, messageTimestampStyle,
  initialChatDisplayContainerStyle, initialChatDisplayIconStyle,
  initialChatDisplayTextContainerStyle,
} from '../AiAgent/AiAgnetStyle'; // We'll create this file

const AiAgent = () => {
  const greeting = getGreeting();
  const [status, setStatus] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [noSpeechAttempts, setNoSpeechAttempts] = useState(0);
  const [lastNoSpeechTime, setLastNoSpeechTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  // Refs for current state values in event handlers
  const conversationStartedRef = useRef(conversationStarted);
  const isSpeakingRef = useRef(isSpeaking);
  const isListeningRef = useRef(isListening);
  const messagesEndRef = useRef(null);
  const userIdRef = useRef(null);
  const noSpeechAttemptsRef = useRef(noSpeechAttempts);
  const lastNoSpeechTimeRef = useRef(lastNoSpeechTime);
  const fileInputRef = useRef(null);
  const isTypingRef = useRef(isTyping);

  // Update refs when state changes
  useEffect(() => { conversationStartedRef.current = conversationStarted; }, [conversationStarted]);
  useEffect(() => { isSpeakingRef.current = isSpeaking; }, [isSpeaking]);
  useEffect(() => { isListeningRef.current = isListening; }, [isListening]);
  useEffect(() => { noSpeechAttemptsRef.current = noSpeechAttempts; }, [noSpeechAttempts]);
  useEffect(() => { lastNoSpeechTimeRef.current = lastNoSpeechTime; }, [lastNoSpeechTime]);
  useEffect(() => { isTypingRef.current = isTyping; }, [isTyping]);

  // Auto scroll to bottom for messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addMessage = (content, isUser = false) => {
    const newMessage = {
      id: Date.now(),
      content,
      isUser,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleNoSpeechFallback = async () => {
    const currentTime = Date.now();
    const timeSinceLastMessage = currentTime - lastNoSpeechTimeRef.current;

    if (timeSinceLastMessage >= 3000) {
      const fallbackMessages = [
        "I didn't catch that. You can type your question below or try speaking again.",
        "Having trouble hearing you. Feel free to type your message or speak a bit louder.",
        "No voice detected. You can use the text input at the bottom or try speaking again.",
      ];

      const currentAttempts = noSpeechAttemptsRef.current;
      const messageIndex = Math.min(currentAttempts, fallbackMessages.length - 1);
      const fallbackMsg = fallbackMessages[messageIndex];

      addMessage(fallbackMsg, false);
      setStatus('Voice not detected - text input available');
      setLastNoSpeechTime(currentTime);

      await playWithSpeechSynthesis(fallbackMsg, setIsSpeaking); // Pass setIsSpeaking for internal state management
      setTimeout(() => {
        if (conversationStartedRef.current && !isSpeakingRef.current && !isUploading && !isTypingRef.current) {
          startListening();
        }
      }, 500);
    } else {
      setTimeout(() => {
        if (conversationStartedRef.current && !isSpeakingRef.current && !isUploading && !isTypingRef.current) {
          startListening();
        }
      }, 2000);
    }
  };
  
  const stopAudioResponse = () => {
  if ('speechSynthesis' in window) {
    speechSynthesis.cancel();
    setIsSpeaking(false);
    setStatus('Audio stopped');
    
    // Resume listening after stopping audio
    setTimeout(() => {
      if (conversationStartedRef.current && !isUploading && !isTypingRef.current) {
        startListening();
      }
    }, 500);
  }
};

const onSpeechResult = async (transcript) => {
  if (isSpeakingRef.current || isUploading || isTypingRef.current) {
    console.log('AI is speaking, uploading, or user is typing, ignoring user voice input');
    return;
  }
  
  if (transcript) {
    // Check if user said "stop" while AI is speaking
    if (transcript.toLowerCase().includes('stop') && isSpeakingRef.current) {
      stopAudioResponse();
      return;
    }
    
    handleQuery(transcript, true);
  }
};

  const onSpeechError = (error, isListeningCurrently) => {
    setIsListening(false);
    if (error === 'no-speech') {
      if (!isTypingRef.current) {
        setNoSpeechAttempts(prev => prev + 1);
        handleNoSpeechFallback();
      } else {
        setTimeout(() => {
          if (conversationStartedRef.current && !isSpeakingRef.current && !isUploading && !isTypingRef.current) {
            startListening();
          }
        }, 2000);
      }
    } else if (error === 'network') {
      setStatus('Network error. Please check your connection.');
      const networkMsg = 'Network error detected. You can continue using text input below.';
      addMessage(networkMsg, false);
      playWithSpeechSynthesis(networkMsg, setIsSpeaking);
    } else if (error === 'not-allowed' || error === 'denied') {
      setStatus('Microphone is not connected please connect your microphone and wait for 10 seconds.');
      // addMessage('Microphone access denied. Please check your browser settings.', false);
    } else {
      setStatus('Recognition error. Restarting...');
      setTimeout(() => {
        if (conversationStartedRef.current && !isSpeakingRef.current && !isUploading && !isTypingRef.current) {
          startListening(); 
        }
      }, 2000);
    }
  };

  const onSpeechStart = () => {
    if (!isTypingRef.current) {
      setStatus('🎙️ Listening...');
    }
    setIsListening(true);
  };

  const onSpeechEnd = () => {
    setIsListening(false);
    setTimeout(() => {
      if (conversationStartedRef.current && !isSpeakingRef.current && !isUploading && !isTypingRef.current) {
        startListening();
      } else {
        if (isUploading) {
          setStatus('Uploading file...');
        } else if (isTypingRef.current) {
          setStatus('User is typing...');
        } else if (conversationStartedRef.current && !isSpeakingRef.current) {
          setStatus('Ready for your input');
        } else {
          setStatus('Click "Start Conversation" to begin');
        }
      }
    }, 100);
  };

  const { startListening, stopListening, recognition } = useVoiceRecognition({
    onResult: onSpeechResult,
    onError: onSpeechError,
    onStart: onSpeechStart,
    onEnd: onSpeechEnd,
    isListening: isListening, // Pass current listening state
    isSpeaking: isSpeaking,   // Pass current speaking state
    isUploading: isUploading, // Pass current uploading state
    isTyping: isTyping        // Pass current typing state
  });


  // This handles both voice and text queries
// This handles both voice and text queries
const handleQuery = async (text, fromVoice = false) => {
  stopListening(); // Always stop listening before processing a query

  addMessage(text, true);
  setStatus('💡 Processing...');

  if (fromVoice) {
    setNoSpeechAttempts(0);
  }

  try {
    const randomUserId = userIdRef.current;
    const response = await fetch('http://ai.salesninjacrm.com/rag/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question: text,
        user_id: randomUserId,
        session_id:randomUserId
      }),
    });

    const data = await response.json();
    const answer = data.answer || "Sorry, I couldn't find an answer.";

    addMessage(answer, false);
    await playWithSpeechSynthesis(answer, setIsSpeaking); // Play response and update speaking state

    // Resume listening after AI finishes speaking
    setTimeout(() => {
      if (conversationStartedRef.current && !isSpeakingRef.current && !isUploading && !isTypingRef.current) {
        startListening();
      }
    }, 500);

  } catch (err) {
    console.error('AI Error:', err);
    const errorMsg = "Sorry, something went wrong.";
    addMessage(errorMsg, false);
    await playWithSpeechSynthesis(errorMsg, setIsSpeaking); // Play error message
    
    // Resume listening after error message
    setTimeout(() => {
      if (conversationStartedRef.current && !isSpeakingRef.current && !isUploading && !isTypingRef.current) {
        startListening();
      }
    }, 500);
  }
};

  const handleTextSubmit = (e) => {
    if (e) e.preventDefault();
    if (textInput.trim() && conversationStarted && !isSpeaking && !isUploading) {
      handleQuery(textInput.trim(), false);
      setTextInput('');
      setIsTyping(false);
    }
  };

  const startConversation = async () => {
    if (conversationStarted) return;

    userIdRef.current = generateRandomUserId();
    setMessages([]);
    setNoSpeechAttempts(0);
    setLastNoSpeechTime(0);
    setIsTyping(false);

    const welcome = `${greeting}, I'm your voice assistant RedVision AI. You can speak now or type your questions below.`;
    setConversationStarted(true);
    setIsListening(false);

    addMessage(welcome, false);

    await playWithSpeechSynthesis(welcome, setIsSpeaking);
    setTimeout(() => {
      startListening();
    }, 500);
  };

 const endConversation = async () => {
  if (!conversationStarted) return;

  const goodbye = "Thank you for chatting. Goodbye!";
  setConversationStarted(false);
  setIsListening(false);
  setIsSpeaking(false);
  setIsTyping(false);
  setNoSpeechAttempts(0);
  setLastNoSpeechTime(0);

  if (recognition) {
    recognition.stop();
  }
  if ('speechSynthesis' in window) {
    speechSynthesis.cancel();
  }

  addMessage(goodbye, false);
  await playWithSpeechSynthesis(goodbye, setIsSpeaking);

  // Call the disconnect API
  try {
    await fetch('http://ai.salesninjacrm.com/rag/disconnect', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ session_id: userIdRef.current })
    });
  } catch (error) {
    console.error('Error disconnecting session:', error);
  }

  setStatus('Click "Start Conversation" to begin.');
};


  const newChat = () => {
    if (conversationStarted) {
      endConversation();
    }
    setTimeout(() => {
      startConversation();
    }, 500);
  };

  const getStatusMessage = () => {
    if (isUploading) return 'Uploading file...';
    if (isSpeaking) return 'AI is speaking...';
    if (isTyping) return 'User is typing...';
    if (isListening) return 'I\'m listening...';
    if (status) return status;
    if (conversationStarted) return 'Ready for your input';
    return 'Ready to chat';
  };

  const getStatusIcon = () => {
    if (isUploading) return <Upload size={20} style={{ color: '#9d174d', animation: 'pulse 1s infinite' }} />;
    if (isSpeaking) return <Volume2 size={20} style={{ color: '#60a5fa', animation: 'pulse 1s infinite' }} />;
    if (isListening) return <Mic size={20} style={{ color: '#10b981', animation: 'pulse 1s infinite' }} />;
    if (isTyping) return <MessageCircle size={20} style={{ color: '#fbbf24' }} />;
    if (conversationStarted) return <MicOff size={20} style={{ color: '#fbbf24' }} />;
    return <MessageCircle size={20} style={{ color: '#9ca3af' }} />;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF, TXT, and DOC/DOCX files are allowed.');
      return;
    }

    setIsUploading(true);
    setStatus('Uploading file...');
    addMessage(`Uploading file: ${file.name}...`, true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('http://ai.salesninjacrm.com/upload-file', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      addMessage(`File uploaded successfully: ${file.name}. ${result.message || ''}`, false);
      setStatus('File uploaded. Ready for questions.');
    } catch (error) {
      console.error('File upload failed:', error);
      addMessage(`File upload failed: ${file.name}. Error: ${error.message}`, false);
      setStatus('File upload failed.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setTimeout(() => {
        if (conversationStartedRef.current && !isSpeakingRef.current && !isTypingRef.current) {
          startListening();
        }
      }, 1000);
    }
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current && !isUploading) {
      fileInputRef.current.click();
    }
  };

  return (
    <div style={containerStyle}>
      {/* Sidebar */}
      <div style={sidebarStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MessageCircle size={24} style={{ color: '#2563eb' }} />
            </div>
            <div>
              <h1 style={{
                fontWeight: '600',
                color: '#111827',
                margin: 0,
                fontSize: '16px'
              }}>
                 RedVision AI 
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0
              }}>
                How can I help you today?
              </p>
            </div>
          </div>

          {/* Control Buttons */}
          <div>
            <button
              onClick={startConversation}
              disabled={conversationStarted || isUploading}
              style={buttonStyle(conversationStarted || isUploading, '#10b981', '#059669')}
            >
              <Play size={18} />
              Start Chat
            </button>

            <button
              onClick={endConversation}
              disabled={!conversationStarted || isUploading}
              style={buttonStyle(!conversationStarted || isUploading, '#ef4444', '#dc2626')}
            >
              <Square size={18} />
              End Chat
            </button>

            {/* New Chat Button */}
            <button
              onClick={newChat}
              style={newChatButtonStyle}
              disabled={isUploading}
            >
              <Plus size={18} />
              New Chat
            </button>

            {/* Upload File Button in Sidebar */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".pdf,.txt,.doc,.docx"
            />
            <button
              onClick={triggerFileUpload}
              style={uploadSidebarButtonStyle(isUploading)} // ← Add (isUploading) here
              disabled={isUploading}
            >
            {isUploading ? (
              <div className="loader" style={{ border: '2px solid #f3f3f3', borderTop: '2px solid #3498db', borderRadius: '50%', width: '16px', height: '16px', animation: 'spin 1s linear infinite' }}></div>
            ) : (
              <Upload size={18} />
            )}
            {isUploading ? 'Uploading...' : 'Upload File'}
            </button>
          </div>
        </div>
      <button
      onClick={stopAudioResponse}
      disabled={!isSpeaking}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        backgroundColor: 'grey',
        color: '#fff',
        border: 'white',
        borderRadius: '4px',
        cursor: isSpeaking ? 'pointer' : 'not-allowed',
        opacity: isSpeaking ? 1 : 0.5,
        margin:12,
        justifyContent:'center'
      }}
>
  <VolumeX size={18} />
  Stop Audio
</button>

        {/* Status */}
        <div style={statusStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {getStatusIcon()}
            <div style={{ flex: 1 }}>
              <p style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#111827',
                margin: 0
              }}>
                {getStatusMessage()}
              </p>
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                margin: 0
              }}>
                {conversationStarted ? 'Online' : 'Offline'}
              </p>
            </div>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: conversationStarted ? '#10b981' : '#d1d5db',
              animation: conversationStarted ? 'pulse 1s infinite' : 'none'
            }}></div>
          </div>
        </div>

        {/* Voice Visualization */}
        {conversationStarted && (
          <div style={visualizationStyle}>
            <div style={{ textAlign: 'center' }}>
              <div style={micContainerStyle(isSpeaking, isListening, isUploading, isTyping)}>
                {isSpeaking ? (
                  <Volume2 size={32} style={{ color: '#ef4444' }} />
                ) : isListening ? (
                  <Mic size={32} style={{ color: '#10b981' }} />
                ) : isUploading ? (
                  <Upload size={32} style={{ color: '#9d174d' }} />
                ) : isTyping ? (
                  <MessageCircle size={32} style={{ color: '#fbbf24' }} />
                ) : (
                  <Mic size={32} style={{ color: '#3b82f6' }} />
                )}
              </div>

              {(isListening || isSpeaking || isUploading || isTyping) && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '4px',
                  marginBottom: '16px'
                }}>
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: '4px',
                        borderRadius: '2px',
                        backgroundColor: isSpeaking ? '#ef4444' : isListening ? '#10b981' : isUploading ? '#9d174d' : isTyping ? '#fbbf24' : '#10b981',
                        height: `${16 + Math.random() * 16}px`,
                        animation: 'wave 1s infinite',
                        animationDelay: `${i * 0.1}s`
                      }}
                    ></div>
                  ))}
                </div>
              )}

              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0
              }}>
                {isSpeaking
                  ? 'AI is responding...'
                  : isListening
                    ? 'Listening...'
                    : isUploading
                      ? 'Processing upload...'
                      : isTyping
                        ? 'User is typing...'
                        : 'Ready to listen'
                }
              </p>
            </div>
          </div>
        )}

        {/* Features */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            fontSize: '12px',
            color: '#6b7280'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Mic size={14} />
              <span>Voice Input</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Volume2 size={14} />
              <span>Audio Response</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div style={chatAreaStyle}>
        {/* Chat Header */}
        <div style={chatHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MessageCircle size={16} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{
                fontWeight: '600',
                color: '#111827',
                margin: 0,
                fontSize: '16px'
              }}>
                RedVision AI
              </h2>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0
              }}>
                {conversationStarted ? 'Active conversation' : 'Start a conversation'}
              </p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={messagesStyle}>
          {messages.length === 0 ? (
            <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            height: '100vh', // Full viewport height
            padding: '24px',
            backgroundColor: '#f9fafb',
            }}>
            <div style={{
              backgroundColor: '#e5e7eb',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
    <MessageCircle size={32} style={{ color: '#9ca3af' }} />
  </div>

  <div>
    <h3 style={{
      fontSize: '18px',
      fontWeight: '600',
      color: '#111827',
      margin: '0 0 8px 0'
    }}>
      Welcome to RedVision AI
    </h3>
    <p style={{
      color: '#6b7280',
      maxWidth: '400px',
      margin: '0 auto',
      lineHeight: '1.5'
    }}>
      Click "Start Chat" to begin our conversation. I'm here to answer your questions using voice interaction or text input.
    </p>
  </div>
</div>

          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                style={messageContainerStyle(message.isUser)}
              >
                <div style={messageContentStyle(message.isUser)}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: message.isUser ? '#2563eb' : '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {message.isUser ? (
                      <span style={{
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        U
                      </span>
                    ) : (
                      <MessageCircle size={16} style={{ color: 'white' }} />
                    )}
                  </div>
                  <div style={{
                    borderRadius: '16px',
                    padding: '12px 16px',
                    backgroundColor: message.isUser ? '#2563eb' : '#f3f4f6',
                    color: message.isUser ? 'white' : '#111827'
                  }}>
                    <p style={{
                      fontSize: '14px',
                      lineHeight: '1.5',
                      margin: 0,
                      marginBottom: '4px'
                    }}>
                      {message.content}
                    </p>
                    <div style={{
                      fontSize: '12px',
                      color: message.isUser ? 'rgba(255,255,255,0.7)' : '#6b7280'
                    }}>
                      {message.timestamp}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        {conversationStarted && (
          <div style={inputAreaStyle}>
            <div style={inputContainerStyle}>
              <input
                type="text"
                value={textInput}
                onChange={(e) => {
                  const newTextInput = e.target.value;
                  const wasTyping = isTypingRef.current;
                  setTextInput(newTextInput);
                  setIsTyping(newTextInput.length > 0);

                  if (recognition && isListeningRef.current && newTextInput.length > 0) {
                    stopListening();
                  } else if (newTextInput.length === 0 && wasTyping && conversationStartedRef.current && !isSpeakingRef.current && !isUploading) {
                    setTimeout(() => {
                      startListening();
                    }, 100);
                  }
                }}
                onFocus={() => {
                  if (textInput.length > 0) {
                    setIsTyping(true);
                  }
                  if (recognition && isListeningRef.current) {
                    stopListening();
                  }
                }}
                onBlur={() => {
                  setIsTyping(false);
                  if (textInput.length === 0 && conversationStartedRef.current && !isSpeakingRef.current && !isUploading) {
                    setTimeout(() => {
                      startListening();
                    }, 100);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && textInput.trim() && !isSpeaking && !isUploading) {
                    handleTextSubmit(e);
                  }
                }}
                placeholder="Type your message or use voice..."
                style={textInputStyle}
                disabled={isSpeaking || isUploading}
              />
              <button
                onClick={handleTextSubmit}
                disabled={!textInput.trim() || isSpeaking || isUploading}
                style={sendButtonStyle(textInput.trim(), isSpeaking, isUploading)}
              >
                <Send size={16} style={{ color: 'white' }} />
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes wave {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.5); }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AiAgent;